import { useState, useEffect } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Toast from '../components/ui/Toast';
import { fetchWithAdminAuth } from '../utils/adminAuth';

interface IntegrationConfig {
  integration: string;
  state: string;
  settings: Record<string, string>;
}

const INTEGRATIONS_CONFIG = [
  {
    id: 'manychat',
    title: 'ManyChat',
    description: 'Ligação do fluxo Carta da Semana e mensagens automáticas.',
    fields: [
      { key: 'api_key', label: 'API Key', type: 'password' },
      { key: 'page_id', label: 'Page ID', type: 'text' },
      { key: 'flow_id', label: 'Flow ID (Carta da Semana)', type: 'text' }
    ]
  },
  {
    id: 'google_analytics',
    title: 'Google Analytics',
    description: 'ID de medição e eventos principais.',
    fields: [
      { key: 'measurement_id', label: 'Measurement ID', type: 'text' },
      { key: 'api_secret', label: 'API Secret', type: 'password' }
    ]
  },
  {
    id: 'meta_pixel',
    title: 'Meta Pixel',
    description: 'Pixel, eventos de compra e lead.',
    fields: [
      { key: 'pixel_id', label: 'Pixel ID', type: 'text' },
      { key: 'access_token', label: 'Access Token', type: 'password' },
      { key: 'test_event_code', label: 'Test Event Code (opcional)', type: 'text' }
    ]
  },
  {
    id: 'google_ads',
    title: 'Google Ads',
    description: 'Conversões e remarketing.',
    fields: [
      { key: 'conversion_id', label: 'Conversion ID', type: 'text' },
      { key: 'conversion_label', label: 'Conversion Label', type: 'text' }
    ]
  }
];

export default function Integracoes() {
  const [integrations, setIntegrations] = useState<IntegrationConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<typeof INTEGRATIONS_CONFIG[0] | null>(null);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [formState, setFormState] = useState('active');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAdminAuth('/api/admin/integrations');
      if (response.ok) {
        const data = await response.json();
        setIntegrations(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching integrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIntegrationData = (integrationId: string): IntegrationConfig | undefined => {
    return integrations.find(i => i.integration === integrationId);
  };

  const handleConfigure = (config: typeof INTEGRATIONS_CONFIG[0]) => {
    setSelectedIntegration(config);
    const existingData = getIntegrationData(config.id);
    
    const initialForm: Record<string, string> = {};
    config.fields.forEach(field => {
      initialForm[field.key] = existingData?.settings?.[field.key] || '';
    });
    
    setFormData(initialForm);
    setFormState(existingData?.state || 'active');
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!selectedIntegration) return;
    
    try {
      setSaving(true);
      const response = await fetchWithAdminAuth(`/api/admin/integrations/${selectedIntegration.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          settings: formData,
          state: formState
        })
      });
      
      if (response.ok) {
        setToast({ message: 'Integração configurada com sucesso!', type: 'success' });
        setIsModalOpen(false);
        fetchIntegrations();
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      setToast({ message: 'Erro ao guardar configuração', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (integrationId: string) => {
    try {
      const response = await fetchWithAdminAuth(`/api/admin/integrations/${integrationId}/toggle`, {
        method: 'PUT'
      });
      
      if (response.ok) {
        setToast({ message: 'Estado atualizado com sucesso!', type: 'success' });
        fetchIntegrations();
      }
    } catch (error) {
      setToast({ message: 'Erro ao atualizar estado', type: 'error' });
    }
  };

  const isConfigured = (integrationId: string): boolean => {
    const data = getIntegrationData(integrationId);
    return !!data && Object.keys(data.settings || {}).length > 0;
  };

  const isActive = (integrationId: string): boolean => {
    const data = getIntegrationData(integrationId);
    return data?.state === 'active';
  };

  if (loading) {
    return (
      <div className="px-3 sm:px-6 py-4 sm:py-5">
        <Card title="Integrações & Tracking" subtitle="A carregar...">
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-5">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={true}
          onClose={() => setToast(null)}
        />
      )}

      <Card
        title="Integrações & Tracking"
        subtitle="Configure ManyChat, Google Analytics, Google Ads e Meta Pixel."
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {INTEGRATIONS_CONFIG.map((integration) => (
            <div key={integration.id} className="p-4 rounded-lg border border-border-soft bg-[rgba(10,11,20,0.5)]">
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-[0.9rem] font-medium text-white">{integration.title}</h3>
                <div className="flex items-center gap-2">
                  {isConfigured(integration.id) && (
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      isActive(integration.id) 
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                        : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                    }`}>
                      {isActive(integration.id) ? 'Ativo' : 'Inativo'}
                    </span>
                  )}
                </div>
              </div>
              <p className="text-[0.8rem] text-text-soft mb-3">
                {integration.description}
              </p>
              <div className="flex gap-2">
                <Button onClick={() => handleConfigure(integration)}>
                  {isConfigured(integration.id) ? 'Editar' : 'Configurar'}
                </Button>
                {isConfigured(integration.id) && (
                  <Button onClick={() => handleToggle(integration.id)}>
                    {isActive(integration.id) ? 'Desativar' : 'Ativar'}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={`Configurar ${selectedIntegration?.title || ''}`}
      >
        <div className="space-y-4">
          {selectedIntegration?.fields.map((field) => (
            <div key={field.key}>
              <label className="block text-sm text-gray-300 mb-1">{field.label}</label>
              <input
                type={field.type}
                value={formData[field.key] || ''}
                onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                placeholder={field.label}
              />
            </div>
          ))}
          <div>
            <label className="block text-sm text-gray-300 mb-1">Estado</label>
            <select
              value={formState}
              onChange={(e) => setFormState(e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
            >
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
            </select>
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setIsModalOpen(false)}>Cancelar</Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? 'A guardar...' : 'Guardar'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
